/**
 * Single source of truth for amount formatting in the billing module.
 * Mirrors the backend's `formatAmount` helper :
 *   - EUR → `"25,00 €"` (French formatting, symbol after)
 *   - USD → `"$25.00"`  (US formatting, symbol before)
 *
 * Anywhere a billing screen needs to render a price MUST go through this
 * helper instead of hardcoding the currency symbol (`${n} €`). Hardcoded
 * `€` strings on a Shopify shop would mis-render USD amounts as euros —
 * the same currency rule lives backend-side in `currencyForShop()`.
 */

export type BillingCurrency = 'EUR' | 'USD';

export function formatBillingAmount(
  amount: number,
  currency: BillingCurrency,
): string {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
  const value = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${value} €`;
}

/** Symbol-only variant when the caller does its own number formatting. */
export function billingCurrencySymbol(currency: BillingCurrency): '€' | '$' {
  return currency === 'USD' ? '$' : '€';
}
